/**
 * 契約プロジェクトの「人の名簿」。チームとメンバー（運用者コード）を持つ。
 * 画面もサーバーも、名前の解決はここを通す。
 */

export interface TeamInfo {
  id: string
  key: string
  label: string
  sortOrder: number
}

export interface OperatorInfo {
  /** キャンペーン名に入る運用者コード（ko・yu など）。 */
  code: string
  name: string
  teamKey: string | null
  userId: string | null
  /** 一覧の選択肢に出さない（別のコードにまとめて数える）。 */
  hidden: boolean
  /** このコードの数字をまとめて持つコード（國吉＝ku に hay・ok・wat を寄せる等）。 */
  rollsUpTo: string | null
}

export interface DirectoryData {
  teams: TeamInfo[]
  operators: OperatorInfo[]
}

export const OTHER_TEAM_KEY = 'other'

export class Directory {
  readonly teams: TeamInfo[]
  readonly operators: OperatorInfo[]
  private byCode: Map<string, OperatorInfo>
  private byTeam: Map<string, TeamInfo>

  constructor(data: DirectoryData = { teams: [], operators: [] }) {
    this.teams = [...data.teams].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, 'ja'))
    this.operators = data.operators
    this.byCode = new Map(data.operators.map((o) => [o.code.toLowerCase(), o]))
    this.byTeam = new Map(data.teams.map((t) => [t.key, t]))
  }

  operatorName(code?: string | null): string {
    if (!code) return '不明'
    return this.byCode.get(code.toLowerCase())?.name || code
  }

  operatorTeam(code?: string | null): string {
    if (!code) return OTHER_TEAM_KEY
    return this.byCode.get(code.toLowerCase())?.teamKey ?? OTHER_TEAM_KEY
  }

  teamLabel(key?: string | null): string {
    if (!key || key === OTHER_TEAM_KEY) return 'その他'
    return this.byTeam.get(key)?.label ?? key
  }

  teamLabelForOperator(code?: string | null): string {
    return this.teamLabel(this.operatorTeam(code))
  }

  /** 選択肢に出すコード（まとめられる側は出さない）。 */
  selectableCodes(): string[] {
    return this.operators.filter((o) => !o.hidden).map((o) => o.code)
  }

  knownCodes(): string[] {
    return this.operators.map((o) => o.code)
  }

  /** このコードを選んだ時に数に入れるコード（自分＋まとめて持つ相手）。 */
  aliases(code: string): string[] {
    const own = code.toLowerCase()
    const out = new Set([own])
    for (const o of this.operators) {
      if (o.rollsUpTo?.toLowerCase() === own) out.add(o.code.toLowerCase())
    }
    return [...out]
  }

  teamOptions(): Array<{ key: string; label: string }> {
    return this.teams.map((t) => ({ key: t.key, label: t.label }))
  }

  operatorNames(): Record<string, string> {
    return Object.fromEntries(this.operators.map((o) => [o.code, o.name]))
  }

  codesForUser(userId: string): string[] {
    return this.operators.filter((o) => o.userId === userId).map((o) => o.code)
  }
}
